import { ChangeEvent, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import { CheckboxField } from '@qrios/frontend-library';
import {
  COMPLETE_DATE_TIME_FORMAT,
  DateType,
  dateWithTimeSet,
  defaultFromTime,
  defaultToTime,
  formatDate,
  getCurrentMoment,
  isValid
} from '../../../../constants/helpers/dates';
import locales from '../../../../constants/locales';
import DateInput from './dateInput';
import RangePicker from './rangePicker';
import { Dialog } from '../../dialog';
import { useScreenSize } from '../../hooks/useScreenSize';

const useStyles = makeStyles((theme: Theme) => ({
  checkboxNow: {
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginRight: 0,
    marginLeft: theme.spacing(1),
    '& > label': {
      margin: 0,
      '& > span:first-child': {
        padding: 0,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1)
      }
    }
  }
}));

interface Field {
  name: string;
  value: DateType;
  touched?: boolean;
  error?: string;
}
interface Props {
  from: Field;
  to: Field;
  handleFromChange: (date: any) => void;
  handleToChange: (date: any) => void;
  handleBlur: (e: ChangeEvent) => void;
  variant?: 'nowEnabled' | 'standard';
}
const DateRangePicker = ({ from, to, handleFromChange, handleToChange, handleBlur, variant = 'standard' }: Props) => {
  const [nowSelected, setNowSelected] = useState<boolean>(to.value === 'now');
  const [openPicker, setOpenPicker] = useState(false);
  const screenSize = useScreenSize();
  const handleOpenDialog = () => {
    setOpenPicker(true);
  };

  const handleCloseDialog = () => {
    setOpenPicker(false);
  };

  const handleSubmit = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    /**
     * We need to wait for change resolving to avoid strange error clearing behavior,
     * and change modal as the last stack event
     */
    setTimeout(() => {
      handleToChange(
        nowSelected ? to.value : formatDate(dateWithTimeSet(endDate, defaultToTime), COMPLETE_DATE_TIME_FORMAT)
      );
    }, 0);

    setTimeout(() => {
      handleFromChange(formatDate(dateWithTimeSet(startDate, defaultFromTime), COMPLETE_DATE_TIME_FORMAT));
      handleCloseDialog();
    }, 0);
  };

  const handleNowValueChange = () => {
    handleToChange(nowSelected ? getCurrentMoment() : 'now');
    setNowSelected(prevState => !prevState);
  };
  const classes = useStyles({ nowSelected });
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        mb={2}
        {...(screenSize === 'mobile' && {
          flexDirection: 'column',
          marginRight: 0
        })}
      >
        <>
          <DateInput
            name={from.name}
            handleChange={handleFromChange}
            handleBlur={handleBlur}
            handleRangePickerOpen={handleOpenDialog}
            value={from.value as string}
            error={from.error}
            touched={from.touched}
            placeholder={locales.reports.startingDate.placeholder}
            label={locales.reports.startingDate.label}
            style={{
              marginRight: '8px',
              ...{
                ...(screenSize === 'mobile' && {
                  marginRight: '0',
                  marginBottom: '16px'
                })
              }
            }}
            data-testid="picker-start-date"
          />

          <DateInput
            name={to.name}
            disabled={nowSelected}
            handleChange={handleToChange}
            handleBlur={handleBlur}
            handleRangePickerOpen={handleOpenDialog}
            value={to.value === 'now' ? null : ((to.value as unknown) as string)}
            error={to.error}
            touched={to.touched}
            placeholder={locales.reports.endDate.placeholder}
            label={locales.reports.endDate.label}
            data-testid="picker-end-date"
          />
        </>

        {variant === 'nowEnabled' && (
          <CheckboxField
            className={classes.checkboxNow}
            handleChange={handleNowValueChange}
            label="Now"
            name="now"
            checked={nowSelected}
            data-testid="checkbox-now"
          />
        )}
        <Dialog title="Select date range" handleClose={handleCloseDialog} open={openPicker}>
          <RangePicker
            from={isValid(new Date(from.value)) ? from.value : new Date()}
            to={isValid(new Date(to.value)) ? to.value : new Date()}
            nowSelected={nowSelected}
            openPicker={openPicker}
            handleSubmit={handleSubmit}
            handleCloseDialog={handleCloseDialog}
          />
        </Dialog>
      </Box>
    </>
  );
};

export default DateRangePicker;
